import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import useSEO from '@/hooks/useSEO';
import { Button } from '@/components/ui/button';
import { BadgeCheck, CheckCircle2, Camera, FileText, MapPin, ArrowRight, Sparkles } from 'lucide-react';

/**
 * Sales-pitch landing page for the "Verified Properties" trust pillar.
 */
export default function WhyVerifiedPropertiesPage() {
  const { language } = useLanguage();
  const es = language === 'es';
  const t = (en, esT) => (es ? esT : en);

  useSEO({
    title: t('Verified Properties — Sell Faster on WakaCasa', 'Propiedades Verificadas — Vende Más Rápido en WakaCasa'),
    description: t(
      'WakaCasa verifies the title chain, photos and location of each property. Verified listings get the gold seal, premium ranking, and 3-5x more inquiries.',
      'WakaCasa verifica la cadena titular, fotos y ubicación de cada propiedad. Las propiedades verificadas reciben el sello dorado, posicionamiento prioritario y 3-5x más consultas.',
    ),
    canonical: '/why/verified-properties',
    image: '/sales/verified-listings.jpeg',
  });

  const steps = [
    {
      icon: FileText,
      title: t('1. Submit the title chain', '1. Sube la cadena titular'),
      body: t(
        'Property registration document (Registro Subalterno), most recent property-tax bill, and the deed transferring it to the current owner.',
        'Registro Subalterno, último recibo de derecho de frente y el documento que transfiere la propiedad al titular actual.',
      ),
    },
    {
      icon: Camera,
      title: t('2. On-site photo evidence', '2. Evidencia fotográfica en sitio'),
      body: t(
        'Geotagged photos of the property entrance, common areas and main rooms. Optional drone shot for plots and houses.',
        'Fotos geolocalizadas de la entrada, áreas comunes y ambientes principales. Dron opcional para terrenos y casas.',
      ),
    },
    {
      icon: BadgeCheck,
      title: t('3. WakaCasa Verified seal', '3. Sello WakaCasa Verificada'),
      body: t(
        'The listing gets the gold "Verified Property" seal, ranks higher in search, and is eligible for the Featured carousel and Top-Agents leaderboard.',
        'La propiedad recibe el sello dorado "Propiedad Verificada", aparece arriba en búsqueda y queda elegible para el carrusel Destacado y el ranking de Top-Agentes.',
      ),
    },
  ];

  const bullets = [
    t('More inquiries — verified listings receive 3-5x more contact requests.', 'Más consultas — las propiedades verificadas reciben 3-5x más contactos.'),
    t('Eligibility for the Featured Properties homepage carousel.', 'Elegibilidad para el carrusel Propiedades Destacadas en la portada.'),
    t('Premium search ranking against non-verified competitors.', 'Posicionamiento prioritario frente a competidores no verificados.'),
    t('Diaspora-buyer ready: required for Overseas Buyer escrow flow.', 'Listo para diáspora: requerido en el flujo de compra escrow del exterior.'),
    t('Lower risk of disputes — title is reviewed before going live.', 'Menos riesgo de disputas — el título se revisa antes de publicarse.'),
    t('Public verification page so buyers can audit the evidence themselves.', 'Página pública de verificación para que el comprador audite la evidencia.'),
  ];

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="why-verified-properties-page">
      {/* HERO */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#D4AF37] px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em]">
              <Sparkles className="w-3.5 h-3.5" />
              {t('Verified Properties', 'Propiedades Verificadas')}
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] mb-5">
            {t('Sell faster with the ', 'Vende más rápido con el ')}
            <span className="text-[#D4AF37]">{t('Verified Property', 'Propiedad Verificada')}</span>
            {t(' seal.', ' sello.')}
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed mb-8">
            {t(
              'A WakaCasa-verified listing reassures buyers that the title is real, the photos match the location, and the agent behind it has done the legal homework. Every additional trust signal converts.',
              'Una propiedad verificada por WakaCasa le asegura al comprador que el título es real, que las fotos corresponden al lugar, y que el agente detrás hizo la tarea legal. Cada señal de confianza extra convierte.',
            )}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/dashboard">
              <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold h-12 px-7 rounded-xl gap-2" data-testid="why-properties-cta-primary">
                {t('Verify a property', 'Verificar una propiedad')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/search">
              <Button variant="ghost" className="text-white hover:bg-white/10 h-12 px-6 rounded-xl">
                {t('See verified listings', 'Ver propiedades verificadas')}
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-white/50 font-medium">
            <span>⚡ {t('72h review', 'Revisión en 72h')}</span>
            <span>🛰️ {t('Geolocated photo proof', 'Pruebas fotográficas geolocalizadas')}</span>
            <span>💎 {t('Valid 12 months', 'Válido 12 meses')}</span>
          </div>
        </div>
      </section>

      {/* SCREENSHOT + DESCRIPTION */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-3 bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-2xl blur-xl" />
            <img
              src="/sales/property-detail.jpeg"
              alt={t('Property detail with verified badge', 'Detalle de propiedad con sello verificado')}
              className="relative rounded-2xl shadow-2xl border border-slate-200 w-full"
              loading="lazy"
              data-testid="why-properties-screenshot"
            />
          </div>
          <div className="order-1 lg:order-2">
            <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3">
              {t('How buyers see it', 'Cómo lo ve el comprador')}
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl text-[#0B192C] font-semibold mb-4">
              {t('The gold seal that converts.', 'El sello dorado que convierte.')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t(
                'On every listing card, search result and property page, your verified listings carry the unmistakable gold seal. Buyers — especially overseas buyers — filter for it.',
                'En cada tarjeta de búsqueda, resultado y página de propiedad, tus listados verificados llevan el inconfundible sello dorado. Los compradores —especialmente los del exterior— filtran por él.',
              )}
            </p>
            <p className="text-sm text-slate-500 leading-relaxed">
              {t(
                'Tap the seal and the buyer sees exactly what we checked, when it was verified, and which document each fact came from. Full transparency.',
                'Al tocar el sello, el comprador ve exactamente qué revisamos, cuándo, y de qué documento salió cada dato. Transparencia total.',
              )}
            </p>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
          <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3 text-center">
            {t('3 simple steps', '3 pasos simples')}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#0B192C] font-semibold mb-12 text-center">
            {t('How property verification works', 'Cómo funciona la verificación')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-testid={`why-properties-step-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#0B192C] text-[#D4AF37] flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-base text-[#0B192C] font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE EXAMPLE */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-2">
                {t('See it live', 'Míralo en vivo')}
              </p>
              <h2 className="font-heading text-2xl text-[#0B192C] font-semibold">
                {t('Verified listings in Caracas', 'Propiedades verificadas en Caracas')}
              </h2>
            </div>
            <Link to="/search?city=Caracas">
              <Button variant="ghost" className="text-[#0B192C] hover:text-[#D4AF37] font-medium gap-2">
                {t('Open search', 'Abrir búsqueda')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <img
            src="/sales/verified-listings.jpeg"
            alt={t('Verified listings in search', 'Propiedades verificadas en búsqueda')}
            className="rounded-xl border border-slate-100 w-full"
            loading="lazy"
            data-testid="why-properties-listings-screenshot"
          />
        </div>
      </section>

      {/* BULLETS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3">
          {t('What you unlock', 'Qué desbloqueas')}
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl text-[#0B192C] font-semibold mb-8">
          {t('Why agencies verify every listing', '¿Por qué las agencias verifican cada propiedad?')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl p-4">
              <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
              <span className="text-sm text-slate-700 leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA FOOTER */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <BadgeCheck className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">
            {t('Verify your next listing', 'Verifica tu próxima propiedad')}
          </h2>
          <p className="text-white/70 leading-relaxed mb-8 max-w-xl mx-auto">
            {t(
              'USD $19 per property — valid 12 months. Bank Transfer, Pago Móvil or Zelle.',
              'USD $19 por propiedad — válido 12 meses. Transferencia, Pago Móvil o Zelle.',
            )}
          </p>
          <Link to="/dashboard">
            <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold h-12 px-8 rounded-xl gap-2" data-testid="why-properties-cta-footer">
              {t('Go to my dashboard', 'Ir a mi panel')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <p className="text-xs text-white/40 mt-4 flex items-center justify-center gap-1.5">
            <MapPin className="w-3 h-3" />
            {t('Available across all Venezuela', 'Disponible en toda Venezuela')}
          </p>
        </div>
      </section>
    </div>
  );
}
